import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftAssignment } from '../../interfaces/shift/ShiftAssignment';
import { ShiftService } from '../../services/shift.service';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { UserService } from '../../services/user.service';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import AssignmentEditDialog from './AssignmentEditDialog';
import BulkAssignmentDialog from './BulkAssignmentDialog';

interface RowUser {
    id: string;
    username: string;
    name?: string;
    department?: string;
}

const PAGE_SIZE = 20;

const todayStr = () => new Date().toISOString().slice(0, 10);

const ShiftAssignmentPage: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);

    const canAssign = usePermission('SHIFT_ASSIGN', user?.roles?.includes(UserType.InstitutionAdmin) ?? false);
    const canView   = usePermission('SHIFT_VIEW',   true);

    const [asOf, setAsOf] = useState(todayStr());
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [users, setUsers] = useState<RowUser[]>([]);
    const [shiftsById, setShiftsById] = useState<Map<string, Shift>>(new Map());
    const [assignmentByUser, setAssignmentByUser] = useState<Map<string, ShiftAssignment | null>>(new Map());
    const [loading, setLoading] = useState(true);

    const [editingUser, setEditingUser] = useState<RowUser | null>(null);
    const [bulkOpen, setBulkOpen] = useState(false);

    // Debounce search
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearchChange = (val: string) => {
        setSearch(val);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(0);
        }, 400);
    };

    // Load shifts once on mount
    useEffect(() => {
        if (!selectedInstitutionId) return;
        ShiftService.list(selectedInstitutionId)
            .then(data => {
                const m = new Map<string, Shift>();
                data.forEach(s => m.set(s.id, s));
                setShiftsById(m);
            })
            .catch(err => toast.showApiError(err, t('assignments.loadShiftsFailed')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchData = useCallback(async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const pageData = await UserService.getInstitutionUsers(page, PAGE_SIZE, selectedInstitutionId, debouncedSearch || undefined);
            const content: RowUser[] = (pageData.content || []).map((u: any) => ({
                id: u.id,
                username: u.username,
                name: u.name || undefined,
                department: u.department || u.departmentName || undefined,
            }));
            setUsers(content);
            setTotalElements(pageData.totalElements || 0);

            // Fetch active assignment for each visible user
            const entries = await Promise.all(
                content.map(async (u): Promise<[string, ShiftAssignment | null]> => {
                    const asgn = await ShiftAssignmentService.getActive(selectedInstitutionId, u.id, asOf);
                    return [u.id, asgn];
                })
            );
            setAssignmentByUser(new Map(entries));
        } catch (err) {
            toast.showApiError(err, t('assignments.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [selectedInstitutionId, page, debouncedSearch, asOf, toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAsOfChange = (val: string) => {
        setAsOf(val);
        setPage(0);
    };

    const handleEditClose = () => setEditingUser(null);
    const handleSaved = () => {
        setEditingUser(null);
        fetchData();
    };
    const handleBulkSaved = () => {
        setBulkOpen(false);
        fetchData();
    };

    const formatRange = (asgn: ShiftAssignment | null | undefined): string => {
        if (!asgn) return '—';
        const to = asgn.effectiveTo ?? '∞';
        return `${asgn.effectiveFrom} → ${to}`;
    };

    const shiftCode = (asgn: ShiftAssignment | null | undefined): string => {
        if (!asgn?.shiftId) return '—';
        return shiftsById.get(asgn.shiftId)?.code ?? asgn.shiftId;
    };

    if (!canView) {
        return <Typography sx={{ p: 3 }}>{t('assignments.noPermission')}</Typography>;
    }

    return (
        <Box sx={{ padding: 3 }}>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('assignments.title')}</Typography>
                {canAssign && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setBulkOpen(true)}
                        data-testid="bulk-assign-btn"
                    >
                        {t('assignments.bulkAssign')}
                    </Button>
                )}
            </Box>

            {/* Filter bar */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <TextField
                    label={t('assignments.asOf')}
                    type="date"
                    size="small"
                    value={asOf}
                    onChange={e => handleAsOfChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-as-of' }}
                    sx={{ width: 180 }}
                />
                <TextField
                    label={t('assignments.searchUser')}
                    size="small"
                    value={search}
                    onChange={e => handleSearchChange(e.target.value)}
                    inputProps={{ 'data-testid': 'filter-search' }}
                    sx={{ width: 240 }}
                />
            </Stack>

            {/* Table */}
            {loading ? (
                <Typography>{t('loading')}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common:fields.name')}</TableCell>
                                <TableCell>{t('common:fields.department')}</TableCell>
                                <TableCell>{t('assignments.columns.currentShift')}</TableCell>
                                <TableCell>{t('assignments.columns.effectiveRange')}</TableCell>
                                {canAssign && <TableCell>{t('assignments.columns.action')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={canAssign ? 5 : 4}>{t('noUsers')}</TableCell>
                                </TableRow>
                            ) : (
                                users.map(u => {
                                    const asgn = assignmentByUser.get(u.id);
                                    return (
                                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                            <TableCell>{u.name || displayUsername(u.username)}</TableCell>
                                            <TableCell>{u.department || '—'}</TableCell>
                                            <TableCell>{shiftCode(asgn)}</TableCell>
                                            <TableCell>{formatRange(asgn)}</TableCell>
                                            {canAssign && (
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        aria-label={t('assignments.editAria')}
                                                        onClick={() => setEditingUser(u)}
                                                        data-testid={`edit-btn-${u.id}`}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            {/* Edit dialog */}
            {editingUser && selectedInstitutionId && (
                <AssignmentEditDialog
                    open={!!editingUser}
                    onClose={handleEditClose}
                    userId={editingUser.id}
                    userName={editingUser.name || displayUsername(editingUser.username)}
                    institutionId={selectedInstitutionId}
                    shifts={Array.from(shiftsById.values())}
                    existingAssignment={assignmentByUser.get(editingUser.id) ?? null}
                    onSaved={handleSaved}
                />
            )}

            {/* Bulk dialog */}
            {selectedInstitutionId && (
                <BulkAssignmentDialog
                    open={bulkOpen}
                    onClose={() => setBulkOpen(false)}
                    institutionId={selectedInstitutionId}
                    shifts={Array.from(shiftsById.values())}
                    users={users}
                    onSaved={handleBulkSaved}
                />
            )}
        </Box>
    );
};

export default ShiftAssignmentPage;
